import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { Box, Card, Flex, Heading, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SectionHeader } from '~/components/section-header';
import { AcceleratorsIcon } from '~/features/enterprise/landing-pages/icons';

/** Max width for the cards row only (Figma). */
const ACCELERATORS_CARDS_MAX_WIDTH = '1440px';

export type AcceleratorsCardConfig = {
  id: string;
  image: {
    src: string;
  };
};

export type AcceleratorsConfig = {
  cards: AcceleratorsCardConfig[];
};

const CARD_HEIGHT = { base: '480px', md: '520px' };
/** Fixed card widths (match testimonial carousel); no shrink on viewport change — scroll parent instead. */
const CARD_WIDTH = { base: '302px', md: '327px' };

type PlatformFeatureCardProps = {
  body: string;
  imageAlt: string;
  imageSrc: string;
  label: string;
  title: string;
};

/**
 * Visual shell aligned with homepage `TestimonialCarousel` cards (image + overlay stack),
 * with platform-feature copy instead of quotes or attribution.
 */
function PlatformFeatureCard({ body, imageAlt, imageSrc, label, title }: PlatformFeatureCardProps) {
  return (
    <Card
      border='1px solid'
      borderColor='hsla(0, 0%, 100%, 1)'
      borderRadius='12px'
      boxShadow='none'
      flex='none'
      flexShrink={0}
      height={CARD_HEIGHT}
      minWidth={CARD_WIDTH}
      overflow='hidden'
      position='relative'
      width={CARD_WIDTH}
    >
      <Image
        alt={imageAlt}
        loading='lazy'
        sizes='(max-width: 767px) 302px, 327px'
        src={imageSrc}
        style={{ objectFit: 'cover', pointerEvents: 'none' }}
        fill
      />
      <Box
        bgGradient='linear(to-b, rgba(7, 9, 10, 0.82) 0%, rgba(0, 0, 0, 0) 100%)'
        height='132px'
        left={0}
        pointerEvents='none'
        position='absolute'
        right={0}
        top={0}
      />
      <Box
        bgGradient='linear(to-t, rgba(7, 9, 10, 0.95) 0%, rgba(0, 0, 0, 0) 99%)'
        bottom={0}
        height='55%'
        left={0}
        pointerEvents='none'
        position='absolute'
        right={0}
      />
      <Flex
        flexDirection='column'
        height='100%'
        justifyContent='space-between'
        position='relative'
        padding={{
          base: '22px',
          md: '26px 24px 24px',
        }}
      >
        <Flex
          alignItems='center'
          gap={3}
          _after={{
            backgroundColor: 'hsla(255, 100%, 100%, 0.4)',
            content: "''",
            display: 'block',
            flex: 1,
            height: '0.5px',
            minWidth: 0,
          }}
        >
          <Heading
            as='p'
            color='white'
            flexShrink={0}
            fontFamily='condensed'
            fontSize='0.9375rem'
            fontWeight='500'
            lineHeight='1.25'
            textTransform='uppercase'
          >
            {label}
          </Heading>
        </Flex>
        <Flex flexDirection='column' gap={{ base: 2, md: 2 }}>
          <Heading as='h3' color='white' fontSize='1.125rem' fontWeight='600' lineHeight='1.2'>
            {title}
          </Heading>
          <Text color='white' fontSize='0.9375rem' lineHeight='1.4'>
            {body}
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
}

export function AcceleratorsSection({ config, slug }: { config: AcceleratorsConfig; slug: string }) {
  const { t } = useTranslation();
  const tPrefix = `enterprise.${slug}.accelerators`;

  return (
    <Box as='section' padding={{ base: '64px 0', md: '64px 0' }}>
      <Flex flexDirection='column' gap={{ base: '40px', md: '56px' }}>
        <ContentContainer contentMaxWidth='content-max-width-xl'>
          <SectionHeader
            color='grey.500'
            iconSlot={<AcceleratorsIcon color='grey.500' height='18px' width='18px' />}
            subtitle={t(`${tPrefix}.subtitle`) as string}
            descriptionSlot={
              <Text color='grey.500' margin='0 auto' maxWidth='843px'>
                {t(`${tPrefix}.description`)}
              </Text>
            }
            titleSlot={
              <Heading
                as='h2'
                color='grey.900'
                fontSize={{ base: '2.25rem', md: '3.25rem' }}
                fontWeight='600'
                letterSpacing='-2px'
                lineHeight='1.2'
              >
                {t(`${tPrefix}.title`)}
              </Heading>
            }
          />
        </ContentContainer>
        <ContentContainer maxW={{ base: '100%', xl: ACCELERATORS_CARDS_MAX_WIDTH }}>
          <Box
            overflowX='auto'
            overflowY='hidden'
            paddingBottom='12px'
            tabIndex={0}
            marginInline={{
              base: '-16px',
              md: '-24px',
              lg: '-48px',
              xl: '0',
            }}
            paddingInline={{
              base: '16px',
              md: '24px',
              lg: '48px',
              xl: '0',
            }}
            sx={{
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
            }}
          >
            <Flex flexShrink={0} flexWrap='nowrap' gap={{ base: '24px', md: '26px' }} width='max-content'>
              {config.cards.map((card) => (
                <PlatformFeatureCard
                  key={card.id}
                  body={t(`${tPrefix}.cards.${card.id}.body`)}
                  imageAlt={t(`${tPrefix}.cards.${card.id}.imageAlt`)}
                  imageSrc={card.image.src}
                  label={t(`${tPrefix}.cards.${card.id}.label`)}
                  title={t(`${tPrefix}.cards.${card.id}.title`)}
                />
              ))}
            </Flex>
          </Box>
        </ContentContainer>
      </Flex>
    </Box>
  );
}
